/*
  VFModal is a pretty complicated form field. It basically works like any other form field underneath though.
  For example, it renders within the form with the standard props and hooks. However what's more complicated
  about it is uses a react portal to render a second FormSection. This being the case, the data it saves is nested
  as well.

  When working with this form field it helps to remember that it is:
  -A form field, and so acts like and does everything a form does
  -Form-like in structure and so also behaves like a whole new form (but built into a form field)


  Meanwhile it is not:
  -A form section (though, as a form, it does contain form sections)
*/

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import uniqid from 'uniqid';
import { ModalPortal } from 'AppSrc/core/components/portals/modal-portal';
import { Alert } from 'AppSrc/core/components/utility/alert';
import { InlineErrors } from './sub-components/inline-errors';
import { FieldTitle } from './sub-components/field-title';
import {
  vFParamsPropShape,
  disabledFieldOptionsShape,
  formDataShape,
} from './data-structure/v-f-proptypes';
import {
  getFieldPropShape,
  formPropShape,
} from './data-structure/formik-proptypes';
import { ModalErrors } from '../modal-errors';
import { ModalSummary } from './summary-components/modal-summary/modal-summary';
import 'AppSrc/core/style/components/form/form-base-components/v-f-modal.css';

const DELETE_ALERT = 'on-delete';
const CANCEL_ALERT = 'cancel-alert';

// #TODO
// This should be gotton from common-logic but the dependancy cycle makes this impossible right now
const currentSelectedNetwork = (formData) => {
  const network = formData.dataByFormSectionId.CHANGE_TYPE.dataByFieldId.select_network.value[0];

  if (!network) {
    console.error('Unable to deterimine network at "currentSelectedNetwork" from "v-f-modal.jsx"');
  }

  return network;
};

const getAlertData = (params) => {

  const {
    alertType,
    option,
    onRowDeletionAlert,
    onCancelAlert,
  } = params;

  if (alertType === DELETE_ALERT) {
    return ({
      option,
      ...onRowDeletionAlert,
    });
  }

  if (alertType === CANCEL_ALERT) {
    return ({
      option,
      ...onCancelAlert,
    });
  }

  return null;
};

// Props for the ModalOptions in FormSection.
const getModalOptions = (params) => {
  const {
    modalDisplayData,
    closeModal,
    openCancelAlert,
    openDeleteAlert,
    vFParams,
  } = params;

  if (!modalDisplayData) {
    return null;
  }

  const {
    getModalOptionsOverwrite,
  } = vFParams;

  // FN: 27/05/2020
  // Kind of ugly to have to do it this way but the navigation *has* to come from formik as formik
  // doesn't return it's promises. As such we have to define all the navigation options. Which,
  // aren't the same for every type of modal
  if (getModalOptionsOverwrite) {
    return getModalOptionsOverwrite({
      modalDisplayData,
      closeModal,
      openCancelAlert,
      openDeleteAlert,
    });
  }


  const {
    // workInfoItemId needs to be removed in #VTO-757
    workInfoItemId,
    type,
  } = modalDisplayData;

  return {
    add: type === 'add' ? true : false, // add is bool as FormSection handles various hooks before calling handleSuccessfulSubmit
    saveChanges: type === 'edit' ? true : false, // saveChanges is bool as FormSection handles various hooks before calling handleSuccessfulSubmit
    cancel: type === 'edit' ? openCancelAlert : closeModal,
    // dont allow delete item if there is an existing workInfo id
    deleteItem: type === 'edit' && !workInfoItemId ? openDeleteAlert : null,
  };
};


const getNewFormSectionsByDisplayOrder = (value, sectionId) => {

  // if the item is already in the array of formSectionsByDisplayOrder, we
  // don't add it again
  if (value.formSectionsByDisplayOrder.includes(sectionId)) {
    return [
      ...value.formSectionsByDisplayOrder,
    ];
  }
  return [
    ...value.formSectionsByDisplayOrder,
    sectionId,
  ];
};

const removeSectionId = (value, sectionIdToDelete, onRowDeletion) => {

  const newValue = {
    formSectionsByDisplayOrder: [],
    dataByFormSectionId: {},
  };
  value.formSectionsByDisplayOrder.forEach((sectionId, i) => {
    if (sectionId !== sectionIdToDelete) {
      newValue.formSectionsByDisplayOrder.push(sectionId);
      newValue.dataByFormSectionId[sectionId] = value.dataByFormSectionId[sectionId];
      return null; // no-op out of this iteration but continues to the next as it's forEach
    }
    if (onRowDeletion) {
      onRowDeletion({
        value,
        sectionIdToDelete,
      });
    }
    return null;
  });

  return newValue;
};




class VFModal extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      modalDisplayData: null,
      alertType: null,
      alertOption: null,
    };
    this.createItem = this.createItem.bind(this);
    this.updateItem = this.updateItem.bind(this);
    this.editItem = this.editItem.bind(this);
    this.deleteItem = this.deleteItem.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.resolveAlert = this.resolveAlert.bind(this);
    this.openDeleteAlert = this.openDeleteAlert.bind(this);
    this.openCancelAlert = this.openCancelAlert.bind(this);
  }

  componentDidMount() {
    this.ifDisabledSetDefaultValues();
  }

  componentDidUpdate() {
    this.ifDisabledSetDefaultValues();
  }

  createItem() {
    this.setState({
      modalDisplayData: {
        sectionId: uniqid(),
        type: 'add',
      },
    });
  }

  editItem(sectionId) {
    // get the existing workInfoItemId out and add it to the modalDisplayData
    const { value } = this.props.field;
    const workItemData = value ? value.dataByFormSectionId[sectionId] : null;
    // workInfoItemId needs to be removed in #VTO-757
    const workInfoItemId = workItemData ? workItemData.workInfoItemId : null;
    this.setState({
      modalDisplayData: {
        workInfoItemId,
        sectionId,
        type: 'edit',
      },
    });
  }

  updateItem(data) {

    const {
      field,
    } = this.props;

    const {
      modalDisplayData,
    } = this.state;

    const { sectionId } = modalDisplayData;

    const {
      name,
      value,
    } = field;

    const newFormSectionsByDisplayOrder = getNewFormSectionsByDisplayOrder(value, sectionId);
    const currentSectionData = value.dataByFormSectionId[sectionId];

    // Merge the data from formik into the existing section data
    // This allows any non-field values, such as workInfoItemId or relationshipId
    // to be maintained
    const newSectionData = {
      ...currentSectionData,
      ...data,
    };

    // add the new section data and sectionsByDisplayOrder into the data to be saved
    const dataToSave = {
      formSectionsByDisplayOrder: newFormSectionsByDisplayOrder,
      dataByFormSectionId: {
        ...value.dataByFormSectionId,
        [sectionId]: newSectionData,
      },
    };
    this.props.form.setFieldValue(name, dataToSave);
    this.props.form.setFieldTouched(name, true);
    this.setState({
      modalDisplayData: null,
    });
  }


  deleteItem(sectionId) {
    const {
      field,
      vFParams,
    } = this.props;

    const {
      name,
      value,
    } = field;

    const {
      onRowDeletion,
    } = vFParams;

    const newValue = removeSectionId(value, sectionId, onRowDeletion);

    this.props.form.setFieldValue(name, newValue);
    this.props.form.setFieldTouched(name, true);
    this.setState({
      modalDisplayData: null,
    });
  }

  openDeleteAlert(params) {
    const { // whilst this is only has one param, we need to check that is explicitely the param we want and not an event
      option,
    } = params;

    this.setState({
      alertType: DELETE_ALERT,
      alertOption: option ? option : null,
    });
  }

  openCancelAlert() {
    this.setState({
      alertType: CANCEL_ALERT,
    });
  }

  resolveAlert(params) {

    const {
      boolean,
      option,
    } = params;

    const {
      alertType,
    } = this.state;

    this.setState({
      alertType: null,
      alertOption: null,
    });


    // if they choose cancel no-op after quitting out of the alert modal
    if (boolean === false) {
      return null;
    }


    if (alertType === DELETE_ALERT) {
      if (option) {
        this.deleteItem(option);
        return null;
      }

      if (this.state.modalDisplayData && this.state.modalDisplayData.sectionId) {
        this.deleteItem(this.state.modalDisplayData.sectionId);
        return null;
      }

      console.error(`UNABLE TO RESOLVE WHICH WII TO RESOLVE WHICH WORK INFORMATION ITEM TO DELETE WITH BOOLEAN: ${boolean} OPTION: ${option}`);
      return null;
    }

    if (alertType === CANCEL_ALERT) {
      this.closeModal();
    }

    return null;
  }


  ifDisabledSetDefaultValues() {
    const {
      name,
      value,
    } = this.props.field;

    const {
      disabled,
      disabledValue,
    } = this.props.disabledFieldOptions;

    if (!disabled) {
      return null;
    }

    if (JSON.stringify(value) !== JSON.stringify(disabledValue)) {
      this.props.form.setFieldValue(name, disabledValue);
      this.props.form.setFieldTouched(name, true);
    }
    return null;
  }

  closeModal() {
    this.setState({
      modalDisplayData: null,
    });
  }

  render() {

    const {
      modalDisplayData,
      alertType,
      alertOption,
    } = this.state;

    const {
      vFParams,
      field,
      form,
      disabledFieldOptions,
      optional,
      outerFormData,
    } = this.props;

    const {
      name,
      value,
    } = field;

    const {
      disabled,
    } = disabledFieldOptions;

    const formErrors = form.errors;
    const touched = form.touched[name];


    const errors = formErrors[name];

    const inlineErrors = errors && errors.inlineErrors ? errors.inlineErrors : null;
    const modalErrors = errors && errors.modalErrors ? errors.modalErrors : null;

    const {
      fieldTitle,
      OpenModalComponent,
      ModalSection,
      modalSummaryType,
      modalErrorsTitle,
      onRowDeletionAlert,
      onCancelAlert,
    } = vFParams;

    const alertData = getAlertData({
      alertType,
      option: alertOption,
      onRowDeletionAlert,
      onCancelAlert,
    });


    const modalOptions = getModalOptions({
      modalDisplayData,
      closeModal: this.closeModal,
      openCancelAlert: this.openCancelAlert,
      openDeleteAlert: this.openDeleteAlert,
      vFParams,
    });


    const className = `v-f-field-type v-f-modal${disabled ? ' v-f-modal-disabled' : ''} v-f-modal-${name}`;

    const hasValue = value.formSectionsByDisplayOrder.length > 0;


    const formData = {
      formSectionsByDisplayOrder: [
      ],
      dataByFormSectionId: {
      },
      ...value,
    };

    const selectedNetwork = currentSelectedNetwork(outerFormData);

    return (
      <div
        className={className}
      >
        { // Errors that need resolving through the modal. Such as mandatory formfields that need completed in the nested data
          modalErrors && (
            <ModalErrors
              modalErrorsTitle={modalErrorsTitle}
              errors={modalErrors}
            />
          )
        }

        <OpenModalComponent
          openModal={this.createItem}
          disabled={disabled}
        />
        {
          fieldTitle && (
            <FieldTitle
              fieldTitle={fieldTitle}
              optional={optional}
            />
          )
        }
        {
          modalDisplayData && (
            <ModalPortal>
              <div
                className={`v-f-modal-section v-f-modal-section-${name}`}
              >
                <div className="v-f-modal-shadow" />
                <div className="v-f-modal-inner">
                  <ModalSection
                    handleSuccessfulSubmit={this.updateItem}
                    modalDisplayData={modalDisplayData}
                    formData={formData}
                    outerFormData={outerFormData}
                    modalOptions={modalOptions}
                  />
                </div>
              </div>
            </ModalPortal>
          )
        }
        {
          inlineErrors && touched && (
            <InlineErrors
              inlineErrors={inlineErrors}
            />
          )
        }
        {
          hasValue && modalSummaryType && (
            <ModalSummary
              modalSummaryType={modalSummaryType}
              modalData={value}
              editItem={this.editItem}
              deleteItem={this.openDeleteAlert}
              selectedNetwork={selectedNetwork}
              formData={outerFormData}
            />
          )
        }
        {
          alertData && (
            <Alert
              alertData={alertData}
              resolveAlert={this.resolveAlert}
            />
          )
        }
      </div>
    );
  }
}

VFModal.propTypes = {
  vFParams: vFParamsPropShape,
  // we have to define the value within the data structure for field
  field: getFieldPropShape(formDataShape),
  outerFormData: formDataShape,
  form: formPropShape,
  disabledFieldOptions: disabledFieldOptionsShape,
  optional: PropTypes.bool,
};

const noValueVFModal = (value) => value.formSectionsByDisplayOrder.length < 1;
// This is a reference to the component. This is needed as the value for each form field is displayed on a change summary
// page but this is dependant on what type of form field is used
const VF_MODAL = 'v-f-modal';

export {
  VFModal,
  noValueVFModal,
  VF_MODAL,
};
